import { toast } from 'react-toastify';
import i18n from '../i18n/config';
import isRtl from 'rtl-detect';

/**
 * Bilingual Toast Utility
 * 
 * Shows toast notifications with proper RTL/LTR support
 * Automatically selects the correct message based on current language
 * 
 * Backend API responses include both English and Arabic messages:
 * - error: English error message
 * - errorAr: Arabic error message  
 * - message: English success message
 * - messageAr: Arabic success message
 */

/**
 * Get current language direction
 * @returns {string} 'rtl' or 'ltr'
 */
const getDirection = () => {
  const currentLang = i18n.language || 'en';
  return isRtl.isRtlLang(currentLang) ? 'rtl' : 'ltr';
};

/**
 * Get the appropriate message based on current language
 * @param {string} messageEn - English message
 * @param {string} messageAr - Arabic message
 * @returns {string} The message in the current language
 */
const getLocalizedMessage = (messageEn, messageAr) => {
  const currentLang = i18n.language || 'en';
  return currentLang === 'ar' && messageAr ? messageAr : messageEn;
};

/**
 * Show bilingual error toast
 * Automatically picks the right language based on i18n settings
 * 
 * @param {string} messageEn - English error message
 * @param {string} [messageAr] - Arabic error message (optional)
 * @param {object} [options] - Additional toast options
 * 
 * @example
 * // From API error response
 * bilingualToast.error(
 *   response.data.error,
 *   response.data.errorAr
 * );
 * 
 * @example  
 * // With custom options
 * bilingualToast.error(
 *   'Operation failed',
 *   'فشلت العملية',
 *   { autoClose: 5000 }
 * );
 */
const error = (messageEn, messageAr = null, options = {}) => {
  const direction = getDirection();
  const message = getLocalizedMessage(messageEn, messageAr);
  
  return toast.error(message, {
    position: direction === 'rtl' ? 'top-left' : 'top-right',
    style: {
      direction,
      textAlign: direction === 'rtl' ? 'right' : 'left',
      fontFamily: direction === 'rtl' ? 'Arial, sans-serif' : 'inherit',
    },
    ...options,
  });
};

/**
 * Show bilingual success toast
 * 
 * @param {string} messageEn - English success message
 * @param {string} [messageAr] - Arabic success message (optional)
 * @param {object} [options] - Additional toast options
 * 
 * @example
 * // From API success response
 * bilingualToast.success(
 *   response.data.message,
 *   response.data.messageAr
 * );
 */
const success = (messageEn, messageAr = null, options = {}) => {
  const direction = getDirection();
  const message = getLocalizedMessage(messageEn, messageAr);
  
  return toast.success(message, {
    position: direction === 'rtl' ? 'top-left' : 'top-right',
    style: {
      direction,
      textAlign: direction === 'rtl' ? 'right' : 'left',
      fontFamily: direction === 'rtl' ? 'Arial, sans-serif' : 'inherit',
    },
    ...options,
  });
};

/**
 * Show bilingual info toast
 * 
 * @param {string} messageEn - English info message
 * @param {string} [messageAr] - Arabic info message (optional)
 * @param {object} [options] - Additional toast options
 */
const info = (messageEn, messageAr = null, options = {}) => {
  const direction = getDirection();
  const message = getLocalizedMessage(messageEn, messageAr);
  
  return toast.info(message, {
    position: direction === 'rtl' ? 'top-left' : 'top-right',
    style: {
      direction,
      textAlign: direction === 'rtl' ? 'right' : 'left',
      fontFamily: direction === 'rtl' ? 'Arial, sans-serif' : 'inherit',
    },
    ...options,
  });
};

/**
 * Show bilingual warning toast
 * 
 * @param {string} messageEn - English warning message
 * @param {string} [messageAr] - Arabic warning message (optional)
 * @param {object} [options] - Additional toast options
 */
const warning = (messageEn, messageAr = null, options = {}) => {
  const direction = getDirection();
  const message = getLocalizedMessage(messageEn, messageAr);
  
  return toast.warning(message, {
    position: direction === 'rtl' ? 'top-left' : 'top-right',
    style: {
      direction,
      textAlign: direction === 'rtl' ? 'right' : 'left',
      fontFamily: direction === 'rtl' ? 'Arial, sans-serif' : 'inherit',
    },
    ...options,
  });
};

/**
 * Localized single string for inline alerts / forms (same logic as toast).
 * @param {object} errorResponse - Axios `error.response` or `{ data: { error, errorAr, message } }`
 */
/** CD-87: API error fields can be non-strings (e.g. a raw Sequelize error object);
 *  coerce to text so they are always safe to render / toast. */
const toMessageText = (value, fallback) => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value.message === 'string') return value.message;
  try { return JSON.stringify(value); } catch (_) { return fallback; }
};

export const getLocalizedApiErrorMessage = (
  errorResponse,
  fallbackEn = 'An error occurred',
  fallbackAr = 'حدث خطأ'
) => {
  const data = errorResponse?.data;
  const messageEn = toMessageText(data?.error, null) || toMessageText(data?.message, fallbackEn);
  const messageAr = toMessageText(data?.errorAr, null) || toMessageText(data?.messageAr, fallbackAr);
  return getLocalizedMessage(messageEn, messageAr);
};

/**
 * Helper to show error from API response.
 * Uses `error`, `message`, `errorAr`, `messageAr` on `errorResponse.data`.
 */
const apiError = (errorResponse, fallbackEn = 'An error occurred', fallbackAr = 'حدث خطأ') => {
  const messageEn = toMessageText(errorResponse?.data?.error, null) || toMessageText(errorResponse?.data?.message, fallbackEn);
  const messageAr = toMessageText(errorResponse?.data?.errorAr, null) || toMessageText(errorResponse?.data?.messageAr, fallbackAr);
  return error(messageEn, messageAr);
};

/**
 * Helper to show success from API response
 * Automatically extracts message and messageAr from response
 * 
 * @param {object} response - Axios response object
 * @param {string} [fallbackEn] - Fallback English message
 * @param {string} [fallbackAr] - Fallback Arabic message
 * 
 * @example
 * bilingualToast.apiSuccess(
 *   response,
 *   'Operation successful',
 *   'تمت العملية بنجاح'
 * );
 */
const apiSuccess = (response, fallbackEn = 'Success', fallbackAr = 'نجاح') => {
  const messageEn = response?.data?.message || fallbackEn;
  const messageAr = response?.data?.messageAr || fallbackAr;
  return success(messageEn, messageAr);
};

export const bilingualToast = {
  error,
  success,
  info,
  warning,
  apiError,
  apiSuccess,
  getLocalizedApiErrorMessage,
};

export default bilingualToast;
